import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Button, Chip, CircularProgress, Typography } from "@material-ui/core";
import { api } from "../../services/apiService";
import GuidedPracticeSection from "./guided-practice-section";
import PostSessionQuizReview from "./post-session-quiz-review";
import PostSessionLearning from "./post-session-learning";
import usePageChoice, { studentProgressLocation } from "./use-page-choice";
import { matchesStatus, summaryFor, scoredSummary, usableSummary } from "./post-session-overview";
import usePostSessionQuizzes from "./use-post-session-quizzes";
import { parseUtc } from "./tutor-activity";

const STATE_CHIP = {
  SENT: { label: "Sent", cls: "psq-chip--sent" },
  OPENED: { label: "Link opened", cls: "psq-chip--opened" },
  CLAIMED: { label: "In progress", cls: "psq-chip--claimed" },
  COMPLETED: { label: "Completed", cls: "psq-chip--completed" },
  EXPIRED: { label: "Expired", cls: "psq-chip--expired" },
  REVOKED: { label: "Link withdrawn", cls: "psq-chip--expired" },
  NOT_SHARED: { label: "Progress not shared", cls: "psq-chip--unshared" },
};

const REASON_COPY = {
  TUTOR_EMAIL_NOT_LINKED:
    "We match quizzes by the Medlearnity address you are copied on. Your QLearn account uses a different email, so none can be shown yet.",
  TUTOR_EMAIL_NOT_VERIFIED:
    "Your QLearn account email has not been verified yet. Verify it (or sign in with your Medlearnity Google account) and your post-session quizzes will appear here.",
};

const OPEN_ERROR =
  "Couldn't open this quiz to do together. It may already be completed, or your link with the student may have changed.";

const pad = (n) => String(n).padStart(2, "0");

// "3 questions" / "1 question" — n is a number here.
const countLabel = (n, noun) => `${n} ${noun}${n === 1 ? "" : "s"}`;

const unmatchedCopy = (n) => {
  if (!(n > 0)) return "";
  const verb = n === 1 ? "is" : "are";
  return `${countLabel(n, "recorded email")} could not be matched to a quiz and ${verb} not shown.`;
};

const utcLabel = (date) =>
  `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())} ` +
  `${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())} UTC`;

const localLabel = (date) =>
  new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);

const viewerTimeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "your local time";
  } catch (e) {
    return "your local time";
  }
};

export function When({ value, missing }) {
  if (value === null || value === undefined) return <span className="mtr-sub">{missing}</span>;
  const date = parseUtc(value);
  if (!date) return <span className="mtr-sub">Unavailable</span>;
  return (
    <time dateTime={value} title={utcLabel(date)}>
      {localLabel(date)}
    </time>
  );
}

const NotShared = () => <span className="mtr-sub">Not shared with you</span>;

// A link open is a link event, not proof the student studied. Expiry is not activity.
const activityTime = (row) => [row.sentAt, ...(row.engagementVisible === true
  ? [row.linkFirstOpenedAt, row.linkLastOpenedAt, row.claimedAt, row.completedAt] : [])]
  .filter(value => parseUtc(value)).sort((a, b) => Date.parse(a) - Date.parse(b)).pop() || "";

function ActivityToolbar({ search, setSearch, status, setStatus, view, setView, disabled }) {
  return (
    <div className="psq-toolbar">
      <div className="psq-view-toggle" role="group" aria-label="Activity view">
        {[['student', 'By student'], ['recent', 'Newest first']].map(([value, label]) => (
          <button key={value} type="button" aria-pressed={view === value} disabled={disabled}
            onClick={() => setView(value)}>{label}</button>
        ))}
      </div>
      <label className="psq-search">Search students or quizzes
        <input type="search" role="textbox" value={search} disabled={disabled}
          placeholder="Name, email or quiz topic" onChange={event => setSearch(event.target.value)} />
      </label>
      <label>Quiz status
        <select value={status} disabled={disabled} onChange={event => setStatus(event.target.value)}>
          <option value="all">All statuses</option>
          <option value="NOT_STARTED">Not started</option>
          <option value="IN_PROGRESS">In progress</option>
          {Object.entries(STATE_CHIP).filter(([value]) => value !== 'CLAIMED').map(([value, chip]) => <option key={value} value={value}>{chip.label}</option>)}
        </select>
      </label>
    </div>
  );
}

// The inline "Do together" row under the quiz row being done together:
// opening spinner, retryable open error, or the student's actual quiz.
function TogetherRow({ mine, onRetry, onClose, onFinish }) {
  return (
    <tr className="psq-together-row">
      <td colSpan={4}>
        {mine.status === "opening" && (
          <div className="mtr-loading" role="status" aria-live="polite">
            <CircularProgress size={20} aria-hidden="true" />
            <Typography variant="body2">Opening the quiz…</Typography>
          </div>
        )}
        {mine.status === "error" && (
          <div className="mtr-error" role="alert" data-testid="psq-together-error">
            <Typography variant="body2" component="span">
              {OPEN_ERROR}
            </Typography>{" "}
            <Button className="mtr-btn" variant="outlined" size="small" onClick={() => onRetry(mine.rowKey, mine.name)}>
              Retry
            </Button>{" "}
            <Button className="mtr-btn" size="small" onClick={onClose}>
              Dismiss
            </Button>
          </div>
        )}
        {mine.status === "open" && (
          <>
            <GuidedPracticeSection
              key={mine.quiz.quizId}
              studentUserId={mine.quiz.studentUserId}
              studentName={mine.name}
              preloaded={mine.quiz}
              onFinish={(answers) => onFinish(mine.quiz, answers)}
            />
            <Button className="mtr-btn" size="small" onClick={onClose}>
              Close without saving
            </Button>
          </>
        )}
      </td>
    </tr>
  );
}

/**
 * Tutor-only "Post-session quizzes" (tutor dashboard contract). Lists
 * the post-session quiz emails our sender recorded as sent with THIS tutor
 * copied; the server derives the tutor from the session, so the list call
 * sends no tutor or student identifier. There are deliberately no email actions
 * (no resend / remind / export / link copy), and one fetch per mount — Retry is
 * manual, nothing polls.
 *
 * The read-only reader uses a GET scoped to the row receipt.
 * "Do together", only on rows the server marks
 * `canDoTogether`: it opens the student's actual quiz inline (by opaque rowKey —
 * the server re-derives everything) and Finish saves it as completed on the
 * student's account, after which the list is refetched.
 *
 * Activity is authorized by the server from the trusted session receipt. Account
 * navigation and Do together still require their own live permissions. Missing
 * counts remain unavailable, and link opens are not proof of student activity.
 *
 * `source` is the page's shared usePostSessionQuizzes() result, so the
 * dashboard's activity feed and this tracker cost one request; without it the
 * section loads on its own.
 */
export default function PostSessionQuizzesSection({ source }) {
  const own = usePostSessionQuizzes(!source);
  // null = loading (from first paint), "dark" = backend feature off.
  const { data, error, load, generation } = source || own;
  const [view, setView] = usePageChoice('quizView', 'student');
  const [search, setSearch] = usePageChoice('quizSearch', '');
  const [status, setStatus] = usePageChoice('quizStatus', 'all');
  const [review, setReview] = useState(null);
  const [expanded, setExpanded] = usePageChoice('quizExpanded', {});
  const [refreshing, setRefreshing] = useState(false);

  // The one row being done together: { rowKey, name, status: "opening" | "open" | "error", quiz }.
  const [together, setTogether] = useState(null);
  const togetherSeq = useRef(0);

  useEffect(() => () => {
    togetherSeq.current += 1;
  }, []);

  const openTogether = async (rowKey, name) => {
    togetherSeq.current += 1;
    const mine = togetherSeq.current;
    setTogether({ rowKey, name, status: "opening" });
    let quiz = null;
    try {
      const res = await api.openTutorPostSessionQuiz(rowKey);
      if (res && !res.errMsg && res.quizId && res.studentUserId && Array.isArray(res.questions)) quiz = res;
    } catch {
      // Any failure (network or thrown) renders the same retryable error row.
      quiz = null;
    }
    if (mine !== togetherSeq.current) return;
    setTogether({ rowKey, name, status: quiz ? "open" : "error", quiz });
  };

  const closeTogether = () => {
    togetherSeq.current += 1;
    setTogether(null);
  };

  // Resolves true once the quiz is saved (or was already completed meanwhile —
  // 409 — where the refetched row tells the truth). Ids come from the server's
  // open response, never from the row.
  const finishTogether = async (quiz, answers) => {
    const res = await api.completeQuizTogether(quiz.studentUserId, quiz.quizId, answers);
    const done = (res && !res.errStatus && !res.errMsg) || Number(res?.errStatus) === 409;
    if (!done) return false;
    closeTogether();
    load();
    return true;
  };

  if (data === "dark") return null;

  const rows = data ? data.rows : [];
  const query = search.trim().toLowerCase();
  const filteredRows = rows.filter(row => matchesStatus(row, status)
    && [row.studentEmail, row.studentUserId ? row.studentDisplayName : '', row.quizTitle, ...(row.topics || [])]
      .some(value => String(value || '').toLowerCase().includes(query)));
  const groups = [];
  const byStudent = new Map();
  [...filteredRows]
    .sort((a, b) => view === 'recent' ? (Date.parse(activityTime(b)) || 0) - (Date.parse(activityTime(a)) || 0)
      : String(b.sentAt).localeCompare(String(a.sentAt)))
    .forEach((r) => {
      // Group by email AND the server-authorised student id. A row the server
      // stripped of identity (null studentUserId — e.g. NOT_SHARED) gets its own
      // email-only group, so it can never inherit a sibling row's name or link.
      const userId = typeof r.studentUserId === "string" && r.studentUserId ? r.studentUserId : null;
      const key = view === 'recent' ? 'recent' : JSON.stringify([r.studentEmail || "", userId]);
      if (!byStudent.has(key)) {
        byStudent.set(key, { key, email: view === 'recent' ? 'Recent activity' : r.studentEmail,
          name: null, userId: view === 'recent' ? null : userId, rows: [] });
        groups.push(byStudent.get(key));
      }
      const group = byStudent.get(key);
      if (view !== 'recent' && userId && !group.name) group.name = r.studentDisplayName || null;
      group.rows.push(r);
    });

  const windowDays = data?.windowDays;
  const refresh = async () => {
    if (refreshing || together) return;
    setRefreshing(true);
    const request = generation.current + 1;
    await load();
    if (generation.current === request) setRefreshing(false);
  };

  return (
    <div className="mtr-section psq" data-testid="psq-section">
      <Typography variant="subtitle2" className="mtr-section-title" component="h2">
        Post-session quiz emails
      </Typography>
      <Typography variant="caption" className="mtr-sub">
        Quizzes emailed after your sessions, from email sent to quiz completed, including links not yet
        used. Completions and claims also appear in Recent activity.
      </Typography>

      {data !== 'dark' && <div className="psq-refresh">
        <Button color="inherit" variant="outlined" size="small" disabled={refreshing || data === null || !!together}
          onClick={refresh}>{refreshing ? 'Refreshing activity…' : 'Refresh activity'}</Button>
      </div>}

      {review && <PostSessionQuizReview row={review} onClose={() => setReview(null)} />}

      {!error && rows.length > 0 && <>
        <ActivityToolbar {...{search, setSearch, status, setStatus, view, setView}} disabled={!!together} />
        <div className="psq-summary" aria-label="Visible quiz summary">
          <span><strong>{filteredRows.length}</strong> quizzes shown</span>
          <span><strong>{new Set(filteredRows.map(row => row.studentEmail)).size}</strong> student addresses</span>
          <span><strong>{filteredRows.filter(row => row.engagementVisible && row.state === 'COMPLETED').length}</strong> completed</span>
          <span><strong>{filteredRows.filter(row => row.engagementVisible && row.state === 'CLAIMED').length}</strong> in progress</span>
        </div>
        <p className="mtr-sub">Not started includes sent or opened links. In progress means added to the student’s account; it does not prove questions were answered.</p>
        {view === 'recent' && <p className="mtr-sub">Newest recorded event first. Link opens may include email scanners.</p>}
        {filteredRows.length === 0 && <p role="status">No quizzes match your filters.</p>}
        {(query || status !== 'all') && <Button disabled={!!together} onClick={() => {setSearch(''); setStatus('all');}}>Clear filters</Button>}
      </>}

      {error && (
        <div className="mtr-error" role="alert" data-testid="psq-error">
          <Typography variant="body2" component="span">
            {error}
          </Typography>{" "}
          <Button className="mtr-btn" variant="outlined" size="small" onClick={load}>
            Retry
          </Button>
        </div>
      )}

      {!error && data === null && (
        <div className="mtr-loading" role="status" aria-live="polite">
          <CircularProgress size={20} aria-hidden="true" />
          <Typography variant="body2">Loading post-session quizzes…</Typography>
        </div>
      )}

      {!error && data?.reason && (
        <Typography variant="body2" className="mtr-empty" data-testid="psq-reason">
          {REASON_COPY[data.reason] || "Post-session quizzes can't be matched to your account yet."}
        </Typography>
      )}

      {!error && data && !data.reason && rows.length === 0 && (
        <Typography variant="body2" className="mtr-empty" data-testid="psq-empty">
          No post-session quiz emails recorded for your students
          {windowDays ? ` in the last ${windowDays} days` : ""}. Only emails that were actually sent
          with you copied appear here.
        </Typography>
      )}

      {!error && data && (data.unmatchedCount > 0 || data.truncated) && (
        <Typography variant="body2" className="mtr-sub" data-testid="psq-partial">
          {data.truncated ? "Showing the most recent emails only; older ones are not listed. " : ""}
          {unmatchedCopy(data.unmatchedCount)}
        </Typography>
      )}

      {!error && view === 'recent' && filteredRows.length > 0 && <details className="psq-learning-disclosure">
        <summary>Learning over time for shown quizzes</summary><PostSessionLearning rows={filteredRows} />
      </details>}
      {!error &&
        groups.map((g) => {
          const overview = summaryFor(g.rows);
          const isExpanded = view === 'recent' || (expanded[g.key] ?? groups.length === 1);
          const groupName = g.name || g.email;
          const latestStudy = g.rows.filter(r => r.engagementVisible === true)
            .flatMap(r => [r.completedAt, r.claimedAt]).filter(value => parseUtc(value)).sort().pop();
          return (
          <div className="mtr-card psq-student" key={g.key} data-testid="psq-student">
            <Typography variant="subtitle2" component="h3" className="psq-student-name">
              {g.userId ? (
                <Link to={location => studentProgressLocation(g.userId, location.state)}>{g.name || g.email}</Link>
              ) : (
                g.email
              )}
            </Typography>
            {g.userId && g.name && (
              <Typography variant="caption" className="mtr-sub">
                {g.email}
              </Typography>
            )}
            {view === 'student' && <>
              <div className="psq-student-summary">
                <span>{overview.known ? `${overview.completed}/${overview.known} completed (${Math.round(overview.completed / overview.known * 100)}%)` : 'Progress unavailable'}</span>
                {overview.known > 0 && <span>{overview.outstanding} outstanding</span>}
                {overview.total > 0 && <span>{Math.round(overview.correct / overview.total * 100)}% accuracy · {overview.scored} scored {overview.scored === 1 ? 'quiz' : 'quizzes'}</span>}
                <span>Latest quiz activity: <When value={latestStudy} missing="No claim or completion recorded" /></span>
              </div>
              <Button className="mtr-btn" color="inherit" disabled={!!together} aria-expanded={isExpanded}
                aria-label={`${isExpanded ? 'Hide' : 'Show'} quizzes for ${groupName}`}
                onClick={() => setExpanded(previous => ({...previous, [g.key]: !isExpanded}))}>
                {isExpanded ? 'Hide quizzes' : `Show ${g.rows.length} ${g.rows.length === 1 ? 'quiz' : 'quizzes'}`}
              </Button>
            </>}
            <div hidden={!isExpanded}>
            {view === 'student' && <details className="psq-learning-disclosure"><summary>Learning trends and topics to revisit</summary>
              <PostSessionLearning rows={g.rows} showTopics />
            </details>}
            <table className="psq-table">
              <caption className="psq-sr-only">
                Post-session quiz emails for {g.name ? `${g.name} (${g.email})` : g.email}, newest first
              </caption>
              <thead>
                <tr>
                  <th scope="col">Quiz</th>
                  <th scope="col">Session / sent</th>
                  <th scope="col">Activity</th>
                  <th scope="col">Status / expires</th>
                </tr>
              </thead>
              <tbody>
                {g.rows.flatMap((r) => {
                  const chip = STATE_CHIP[r.state] || { label: "Unknown", cls: "" };
                  const shared = r.engagementVisible === true;
                  const claimed = shared && (r.state === "CLAIMED" || r.state === "COMPLETED" || !!r.claimedAt);
                  const mine = together?.rowKey === r.rowKey ? together : null;
                  const who = r.studentDisplayName || r.studentEmail;
                  return [
                    <tr key={r.rowKey} data-testid="psq-row">
                      <td data-label="Quiz">
                        {view === 'recent' && <span className="psq-row-student">
                          {r.studentUserId ? <Link to={location => studentProgressLocation(r.studentUserId, location.state)}>
                            {r.studentDisplayName || r.studentEmail}</Link> : r.studentEmail}
                          <span className="psq-meta mtr-sub">Latest event: <When value={activityTime(r)} missing="Unavailable" /></span>
                        </span>}
                        <span className="psq-title" data-testid="psq-title">
                          {r.quizTitle}
                        </span>
                        <span className="psq-meta mtr-sub">
                          {typeof r.questionCount === "number"
                            ? countLabel(r.questionCount, "question")
                            : "Question count unavailable"}
                        </span>
                        {(r.topics || []).length > 0 && (
                          <span className="psq-topics">
                            {r.topics.map((t) => (
                              <Chip key={t} size="small" className="psq-topic" label={t} />
                            ))}
                          </span>
                        )}
                      </td>
                      <td data-label="Session / sent">
                        <span className="psq-field-label">Session</span>
                        <span data-testid="psq-session"><When value={r.sessionDate} missing="Unavailable" /></span>
                        <span className="psq-field-label">Sent</span>
                        <span data-testid="psq-sent"><When value={r.sentAt} missing="Unavailable" /></span>
                      </td>
                      <td data-label="Activity">
                        {shared ? <dl className="psq-milestones">
                          <div data-label="Link opened"><dt>Link opened</dt><dd>
                            <When value={r.linkFirstOpenedAt} missing="No open recorded" />
                            {r.linkLastOpenedAt && r.linkLastOpenedAt.slice(0, 16) !== r.linkFirstOpenedAt?.slice(0, 16) && <span className="psq-meta mtr-sub">
                              Latest open: <When value={r.linkLastOpenedAt} missing="Unavailable" />
                            </span>}
                            {r.linkFirstOpenedAt && <span className="psq-meta mtr-sub">
                              {typeof r.linkOpenCount === "number" ? countLabel(r.linkOpenCount, "recorded open") : "Open count unavailable"}
                            </span>}
                          </dd></div>
                          <div><dt>Claimed</dt><dd><When value={r.claimedAt} missing={claimed ? "Yes" : "Not yet"} /></dd></div>
                          <div><dt>Completed</dt><dd><When value={r.completedAt} missing="Not yet" /></dd></div>
                        </dl> : <NotShared />}
                      </td>
                      <td data-label="Status / expires">
                        <Chip size="small" className={`psq-chip ${chip.cls}`} label={chip.label} />
                        {scoredSummary(r) && <span className="psq-score" data-testid="psq-score">
                          {Math.round(r.summary.correct / r.summary.total * 100)}% <span className="mtr-sub">· {r.summary.correct}/{r.summary.total} correct</span>
                        </span>}
                        {shared && r.state === 'COMPLETED' && !scoredSummary(r) && <span className="psq-meta mtr-sub">Score unavailable</span>}
                        {usableSummary(r) && <span className="psq-meta mtr-sub">{r.summary.answered}/{r.summary.total} answered</span>}
                        <span className="psq-meta mtr-sub" data-testid="psq-expires">
                          {!shared && "Not shared with you"}
                          {shared && claimed && "Does not expire once claimed"}
                          {shared && !claimed && r.state === "REVOKED" && "Link withdrawn — no longer usable"}
                          {shared && !claimed && r.state !== "REVOKED" && (
                            <>
                              {r.state === "EXPIRED" ? "Link expired " : "Link expires "}
                              <When value={r.expiresAt} missing="(date unavailable)" />
                            </>
                          )}
                        </span>
                        {r.canReview === true && <Button className="mtr-btn psq-review-btn"
                          variant="contained" color="primary" size="small" disabled={!!together}
                          onClick={() => setReview(r)}
                          aria-label={`${r.state === 'COMPLETED' ? 'Review results' : 'View quiz'}: ${r.quizTitle}`}>
                          {r.state === 'COMPLETED' ? 'Review results' : 'View quiz'}
                        </Button>}
                        {r.canDoTogether === true && !mine && (
                          <Button
                            className="mtr-btn psq-together-btn"
                            variant="outlined"
                            size="small"
                            disabled={!!together}
                            onClick={() => openTogether(r.rowKey, who)}
                            aria-label={`Do together: ${r.quizTitle}`}
                          >
                            Do together
                          </Button>
                        )}
                      </td>
                    </tr>,
                    mine && (
                      <TogetherRow
                        key={`${r.rowKey}-together`}
                        mine={mine}
                        onRetry={openTogether}
                        onClose={closeTogether}
                        onFinish={finishTogether}
                      />
                    ),
                  ];
                })}
              </tbody>
            </table>
            </div>
          </div>
        ); })}

      {!error && rows.length > 0 && (
        <div className="psq-caveats" data-testid="psq-caveats">
          <Typography variant="caption" className="mtr-sub" component="p" data-testid="psq-tz">
            Times shown in {viewerTimeZone()} time; hover a time for the exact UTC value.
          </Typography>
          <dl>
            <dt>Sent</dt>
            <dd>When our system handed the email to the mail service. Not proof it reached the inbox.</dd>
            <dt>Link opened</dt>
            <dd>
              Someone loaded the quiz link while it was active. This can be the student, you (you are
              copied), or an email security scanner. It is not an email open, and opens after a link
              expires are not recorded.
            </dd>
            <dt>Claimed</dt>
            <dd>
              The student signed in and added the quiz to their account. It does not mean they answered
              any questions.
            </dd>
            <dt>Completed</dt>
            <dd>The quiz was finished and submitted.</dd>
            <dt>Expires</dt>
            <dd>
              Unclaimed links stop working on the date shown, which is counted from when the quiz was
              created, slightly before the email went out. Claimed quizzes do not expire.
            </dd>
            <dt>Not shared with you</dt>
            <dd data-testid="psq-caveat-not-shared">
              Activity for your post-session quizzes is shared automatically from the session record.
              Details remain unavailable when access was explicitly declined or revoked, or the
              student account cannot be matched safely. A link open does not require a QLearn account.
            </dd>
          </dl>
        </div>
      )}
    </div>
  );
}
